import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Archive, ArchiveRestore, Download, Loader2, Upload, FolderSearch, FileArchive } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";

const fmt = (n) => `S/ ${Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const title = (q) => q.client?.proyecto || q.name || "Sin proyecto";

export default function ArchivePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [goneTarget, setGoneTarget] = useState(null);

  const load = () => api.get("/quotes/archived").then((r) => setItems(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const available = (items || []).filter((i) => !i.downloaded_at);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = available.length > 0 && available.every((i) => selected.includes(i.id));

  const openRow = (it) => (it.downloaded_at ? setGoneTarget(it) : nav(`/quote/${it.id}`));

  const unarchive = async (id, e) => {
    e.stopPropagation();
    try { await api.post(`/quotes/${id}/unarchive`); toast.success("Cotización devuelta a la lista principal"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.post("/quotes/archived/download", { ids: selected }, { responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "cotizaciones-archivadas.zip";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Descargado ${name}. Guárdalo en tus carpetas locales.`);
      setSelected([]); setConfirmOpen(false); load();
    } catch (err) {
      const msg = err.response?.data instanceof Blob ? JSON.parse(await err.response.data.text()).detail : apiErr(err);
      toast.error(msg || "No se pudo descargar");
    } finally { setDownloading(false); }
  };

  const importBackup = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const body = JSON.parse(await file.text());
      const { data } = await api.post("/quotes/import-backup", body);
      toast.success(data.restored ? "Cotización restaurada desde el respaldo" : "Respaldo importado como nueva cotización");
      load();
    } catch (err) { toast.error(err instanceof SyntaxError ? "El archivo no es un respaldo válido" : apiErr(err)); }
  };

  return (
    <Layout>
      <button data-testid="archive-back" onClick={() => nav("/")}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Volver a cotizaciones
      </button>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 flex items-center gap-3"><Archive className="h-7 w-7" /> Archivo</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Cotizaciones guardadas fuera de la lista principal.{isAdmin ? " Descarga un ZIP (PDF + Excel + respaldo) para liberar espacio." : ""}
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".json,application/json" className="hidden" onChange={importBackup} data-testid="import-backup-input" />
            <button data-testid="import-backup-btn" onClick={() => fileRef.current?.click()}
              className="flex items-center gap-2 border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold px-4 py-2.5 rounded-md transition-colors">
              <Upload className="h-4 w-4" /> Cargar respaldo
            </button>
            <button data-testid="download-zip-btn" onClick={() => setConfirmOpen(true)} disabled={!selected.length}
              className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-4 py-2.5 rounded-md transition-colors disabled:opacity-40">
              <Download className="h-4 w-4" /> Descargar ZIP ({selected.length})
            </button>
          </div>
        )}
      </div>

      {items === null ? (
        <div className="text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando...</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg p-16 text-center bg-white text-slate-500" data-testid="archive-empty">
          No hay cotizaciones archivadas.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="archive-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-3 py-2.5 w-8">
                  {isAdmin && <input type="checkbox" data-testid="archive-select-all" checked={allSelected} disabled={!available.length}
                    onChange={() => setSelected(allSelected ? [] : available.map((i) => i.id))} className="h-4 w-4 accent-emerald-600" />}
                </th>
                <th className="px-4 py-2.5 text-left">Proyecto</th>
                <th className="px-4 py-2.5 text-left hidden sm:table-cell">Cliente</th>
                <th className="px-4 py-2.5 text-right w-20">Puertas</th>
                <th className="px-4 py-2.5 text-right w-40 hidden md:table-cell">Venta total</th>
                <th className="px-4 py-2.5 text-left w-56">Estado</th>
                <th className="px-4 py-2.5 w-28"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid={`archive-row-${it.id}`} onClick={() => openRow(it)}
                  className={`border-b border-slate-100 last:border-0 cursor-pointer ${it.downloaded_at ? "bg-slate-50/60 text-slate-400" : "hover:bg-slate-50"}`}>
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    {isAdmin && !it.downloaded_at && (
                      <input type="checkbox" data-testid={`archive-select-${it.id}`} checked={selected.includes(it.id)}
                        onChange={() => toggle(it.id)} className="h-4 w-4 accent-emerald-600 cursor-pointer" />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-medium block truncate ${it.downloaded_at ? "text-slate-500" : "text-slate-900"}`}>{title(it)}</span>
                    {it.client?.codigo ? <span className="text-xs font-mono text-slate-400">{it.client.codigo}</span> : null}
                  </td>
                  <td className="px-4 py-3 text-slate-500 hidden sm:table-cell truncate">{it.client?.cliente || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{it.total_doors}</td>
                  <td className="px-4 py-3 text-right font-mono hidden md:table-cell">{fmt(it.venta_total)}</td>
                  <td className="px-4 py-3 text-xs">
                    {it.downloaded_at ? (
                      <span data-testid={`archive-downloaded-${it.id}`} className="inline-flex items-center gap-1 font-semibold text-slate-500">
                        <FileArchive className="h-3.5 w-3.5" /> Descargada {it.downloaded_at.slice(0, 10)}
                      </span>
                    ) : (
                      <span className="text-slate-500">Archivada {it.archived_at?.slice(0, 10)}{it.archived_by ? ` · ${it.archived_by}` : ""}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin && !it.downloaded_at && (
                      <button data-testid={`unarchive-${it.id}`} onClick={(e) => unarchive(it.id, e)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700 transition-colors">
                        <ArchiveRestore className="h-3.5 w-3.5" /> Desarchivar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="download-zip-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Download className="h-5 w-5" /> Descargar y liberar espacio</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            Se descargará un ZIP con el PDF, el Excel y un respaldo de <span className="font-semibold">{selected.length}</span> cotización(es).
            Después, sus datos <span className="font-semibold">se quitarán del sistema</span>: solo quedará el nombre en el Archivo.
            Guarda el ZIP en tus carpetas; con el respaldo puedes volver a cargarlas cuando quieras.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button data-testid="download-zip-cancel" onClick={() => setConfirmOpen(false)}
              className="px-4 py-2 rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors">Cancelar</button>
            <button data-testid="download-zip-confirm" onClick={download} disabled={downloading}
              className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 transition-colors disabled:opacity-50">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Descargar ZIP
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!goneTarget} onOpenChange={(o) => { if (!o) setGoneTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="downloaded-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><FolderSearch className="h-5 w-5 text-amber-600" /> Cotización ya descargada</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600" data-testid="downloaded-message">
            <span className="font-semibold text-slate-900">{goneTarget && title(goneTarget)}</span> ya fue descargada el{" "}
            <span className="font-semibold">{goneTarget?.downloaded_at?.slice(0, 10)}</span>
            {goneTarget?.downloaded_by ? <> por {goneTarget.downloaded_by}</> : null} y no se encuentra en el sistema.
            Búscala en tus carpetas locales{goneTarget?.download_file ? <> (archivo <span className="font-mono text-xs">{goneTarget.download_file}</span>)</> : null}.
          </p>
          {isAdmin && <p className="text-xs text-slate-500">Para volver a trabajarla, usa «Cargar respaldo» con el archivo .json incluido en ese ZIP.</p>}
          <div className="flex justify-end pt-2">
            <button data-testid="downloaded-close" onClick={() => setGoneTarget(null)}
              className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 transition-colors">Entendido</button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
